import { DEFAULT_WHY_US_ICON } from "@ckgo2/shared";
import i18n from "@/i18n";
import type { Destination } from "@ckgo2/shared/types";
import { emptyOfferContentForm, type DestinationFormData } from "./types";

/**
 * Převody editoru destinace a lokality: detail z API → hodnoty formuláře → tělo uložení.
 * Čisté funkce bez Reactu a bez `api` klienta, aby šly testovat (`destinationForm.test.ts`).
 *
 * Pravidlo zachování dat: formulář nese jen pole, která editor zobrazuje, a JSON sloupce
 * (`practicalInfo`, `testimonial`, `videoPromo`, `weatherData`, `offerContent`) i položky
 * seznamů přenáší CELÉ, takže klíč, který editor neukazuje (`videoPromo.ctaText`, starý
 * `testimonial.authorImage`), se uložením vrátí beze změny. Sloupec, který formulář nemá
 * (legacy `featuredImage`/`additionalImage`/`galleryImages`, `*Sk`, `garantId`, …), tělo
 * nenese a uložení (PUT = `destinationUpdateSchema`, tedy partial) ho v DB nechá.
 */

/** Detail z `GET /api/admin/destinations/:id`: řádek + `inspirationIds` + `countrySlug`. */
export type DestinationDetail = Destination & { inspirationIds?: string[] };

export const DESTINATION_FORM_DEFAULTS: DestinationFormData = {
  name: "",
  slug: "",
  continent: "asia",
  parentId: "",
  parentInfoText: "",
  guideId: "",
  latitude: "",
  longitude: "",
  shortDescription: "",
  description: "",
  bestTimeToVisit: "",
  climateInfo: "",
  practicalInfo: {},
  highlights: [],
  regions: [],
  cuisine: "",
  activities: [],
  whyWithUs: [],
  additionalContent: "",
  additionalImageId: null,
  featuredImageId: null,
  galleryImageIds: [],
  heroVideoUrl: "",
  moreVideosUrl: "",
  socialReels: [],
  testimonial: {},
  videoPromo: {},
  weatherData: { bestMonths: [], goodMonths: [], temperatures: {}, description: "" },
  heroTitle: "",
  heading: "",
  faqs: [],
  offerContent: emptyOfferContentForm(),
  inspirationIds: [],
  metaTitle: "",
  metaDescription: "",
  sortOrder: 0,
  isFeatured: false,
  isPublished: true,
  showInNav: true,
  showAsCountry: false,
  planningBlockVariant: null,
  planningBlockText: "",
  defaultMinPax: null,
  autoRouteLeads: true,
};

type FaqCategory = DestinationFormData["faqs"][number];
type FlatFaq = FaqCategory["items"][number];

/**
 * Starý plochý tvar FAQ (`[{question, answer}]`) se normalizuje na jednu kategorii.
 * Název kategorie je ze slovníku (týž klíč jako nová kategorie v `FaqEditor`): uloží se
 * a web ho ukáže jako titulek sekce, takže natvrdo anglický text by prosákl na český web.
 */
function faqCategories(faqs: Destination["faqs"]): DestinationFormData["faqs"] {
  if (!Array.isArray(faqs) || faqs.length === 0) return [];
  if ("question" in faqs[0]) {
    return [
      { topic: i18n.t("shared.faqEditor.defaultTopic"), items: faqs as unknown as FlatFaq[] },
    ];
  }
  return faqs;
}

export function destinationFormFromDetail(d: DestinationDetail): DestinationFormData {
  return {
    name: d.name || "",
    slug: d.slug || "",
    continent: d.continent || "asia",
    parentId: d.parentId || "",
    parentInfoText: d.parentInfoText || "",
    guideId: d.guideId || "",
    latitude: d.latitude || "",
    longitude: d.longitude || "",
    shortDescription: d.shortDescription || "",
    description: d.description || "",
    bestTimeToVisit: d.bestTimeToVisit || "",
    climateInfo: d.climateInfo || "",
    practicalInfo: d.practicalInfo || {},
    highlights: d.highlights || [],
    regions: d.regions || [],
    cuisine: d.cuisine || "",
    activities: d.activities || [],
    // Historicky holé věty → záznam bez nadpisu s výchozím piktogramem (web umí obojí).
    // Objektová položka si nechá i klíče, které editor nezobrazuje.
    whyWithUs: (d.whyWithUs || []).map((item) =>
      typeof item === "string"
        ? { icon: DEFAULT_WHY_US_ICON, title: "", description: item }
        : {
            ...item,
            icon: item.icon || DEFAULT_WHY_US_ICON,
            title: item.title || "",
            description: item.description || "",
          },
    ),
    additionalContent: d.additionalContent || "",
    additionalImageId: d.additionalImageId ?? null,
    featuredImageId: d.featuredImageId ?? null,
    galleryImageIds: d.galleryImageIds || [],
    heroVideoUrl: d.heroVideoUrl || "",
    moreVideosUrl: d.moreVideosUrl ?? "",
    socialReels: d.socialReels || [],
    testimonial: d.testimonial || {},
    videoPromo: d.videoPromo || {},
    weatherData: d.weatherData || {
      bestMonths: [],
      goodMonths: [],
      temperatures: {},
      description: "",
    },
    heroTitle: d.heroTitle || "",
    heading: d.heading || "",
    faqs: faqCategories(d.faqs),
    // Chybějící plocha musí zůstat prázdným objektem, ne `undefined` — react-hook-form
    // skládá cesty polí staticky (`offerContent.<klíč>.intro`).
    offerContent: { ...emptyOfferContentForm(), ...(d.offerContent || {}) },
    // Vazbu na inspirace dopočítává detail adminu z `activities.destinationId`.
    inspirationIds: d.inspirationIds || [],
    metaTitle: d.metaTitle || "",
    metaDescription: d.metaDescription || "",
    sortOrder: d.sortOrder || 0,
    isFeatured: d.isFeatured || false,
    isPublished: d.isPublished ?? true,
    showInNav: d.showInNav ?? true,
    showAsCountry: d.showAsCountry ?? false,
    planningBlockVariant: d.planningBlockVariant ?? null,
    planningBlockText: d.planningBlockText ?? "",
    defaultMinPax: d.defaultMinPax ?? null,
    autoRouteLeads: d.autoRouteLeads ?? true,
  };
}

export function destinationSaveBody(data: DestinationFormData) {
  return {
    ...data,
    // Prázdný řádek benefitu (přidaný a nevyplněný) se neukládá — na webu by byl
    // prázdnou kartou s piktogramem.
    whyWithUs: data.whyWithUs.filter((item) => item.title.trim() || item.description.trim()),
    parentId: data.parentId || null,
    // `|| null` (ne `??`): prázdné pole se ukládá jako NULL a lokalita povýšená na zemi
    // ztrácí text infoboxu spolu s rodičem.
    parentInfoText: data.parentId ? data.parentInfoText.trim() || null : null,
    guideId: data.guideId || null,
    // Prázdný vlastní text plánovacího bloku = NULL (platí varianta).
    planningBlockText: data.planningBlockText.trim() || null,
    // Vymazaný „Odkaz na další videa" = NULL; tlačítko webu pak vede na kanál CK Go2.
    moreVideosUrl: data.moreVideosUrl.trim() || null,
  };
}
